/**
 * Comprehensive storage clearing utility
 * Clears all types of browser storage including:
 * - localStorage
 * - sessionStorage
 * - Cookies
 * - Cache API (Service Worker caches)
 * - IndexedDB databases
 * - Application Cache (deprecated)
 */

import Cookies from "universal-cookie";
import { resetSearchAnalyticsSession, SEARCH_DEVICE_ID_KEY } from "./search-analytics-session";

/**
 * Clear all cookies for the current domain
 */
export const clearAllCookies = (): void => {
  try {
    const cookies = document.cookie.split(';');
    
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i];
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      if (name === SEARCH_DEVICE_ID_KEY) {
        continue;
      }
      
      // Try to remove cookie with different path and domain combinations
      const paths = ['/', ''];
      const domains = [window.location.hostname, `.${window.location.hostname}`, ''];
      
      paths.forEach(path => {
        domains.forEach(domain => {
          // Clear with domain
          if (domain) {
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path};domain=${domain}`;
          }
          // Clear without domain
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path}`;
        });
      });
    }
    
    // Also use universal-cookie for known cookies
    try {
      const cookieInstance = new Cookies();
      // Remove all cookies that universal-cookie knows about
      const allCookies = cookieInstance.getAll();
      Object.keys(allCookies).forEach(cookieName => {
        if (cookieName === SEARCH_DEVICE_ID_KEY) {
          return;
        }
        cookieInstance.remove(cookieName, { path: '/' });
        cookieInstance.remove(cookieName, { path: '/', domain: window.location.hostname });
        cookieInstance.remove(cookieName, { path: '/', domain: `.${window.location.hostname}` });
      });
    } catch (error) {
      console.warn('Error clearing cookies with universal-cookie:', error);
    }
  } catch (error) {
    console.error('Error clearing cookies:', error);
  }
};

/**
 * Clear all Cache API caches
 */
export const clearAllCaches = async (): Promise<void> => {
  try {
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => {
          return caches.delete(cacheName);
        })
      );
    }
  } catch (error) {
    console.error('Error clearing Cache API:', error);
  }
};

/**
 * Clear Service Worker cache by sending message to service worker
 */
export const clearServiceWorkerCache = async (): Promise<void> => {
  try {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      return new Promise((resolve, reject) => {
        const messageChannel = new MessageChannel();
        
        messageChannel.port1.onmessage = (event) => {
          if (event.data && event.data.type === 'CACHE_CLEARED') {
            if (event.data.success) {
              resolve();
            } else {
              console.warn('⚠️ Service Worker cache clear had issues:', event.data.error);
              resolve(); // Resolve anyway to not block logout
            }
          }
        };
        
        // Send message to service worker to clear cache
        navigator.serviceWorker.controller?.postMessage(
          { type: 'CLEAR_CACHE' },
          [messageChannel.port2]
        );
        
        // Timeout after 3 seconds
        setTimeout(() => {
          console.warn('⚠️ Service Worker cache clear timeout');
          resolve(); // Resolve anyway to not block logout
        }, 3000);
      });
    }
  } catch (error) {
    console.error('Error clearing Service Worker cache:', error);
  }
};

/**
 * Clear all IndexedDB databases
 */
export const clearAllIndexedDB = async (): Promise<void> => {
  try {
    if ('indexedDB' in window) {
      // Get list of all databases
      if (indexedDB.databases) {
        const databases = await indexedDB.databases();
        await Promise.all(
          databases.map(db => {
            if (db.name) {
              return new Promise<void>((resolve, reject) => {
                const deleteReq = indexedDB.deleteDatabase(db.name!);
                deleteReq.onsuccess = () => {
                  resolve();
                };
                deleteReq.onerror = () => {
                  console.warn(`⚠️ Failed to delete IndexedDB: ${db.name}`);
                  resolve(); // Resolve anyway to not block logout
                };
                deleteReq.onblocked = () => {
                  console.warn(`⚠️ IndexedDB deletion blocked: ${db.name}`);
                  resolve(); // Resolve anyway to not block logout
                };
              });
            }
            return Promise.resolve();
          })
        );
      } else {
        // Fallback for browsers that don't support indexedDB.databases()
        console.warn('⚠️ indexedDB.databases() not supported, skipping IndexedDB clear');
      }
    }
  } catch (error) {
    console.error('Error clearing IndexedDB:', error);
  }
};

/**
 * Clear Application Cache (deprecated but might still exist)
 */
export const clearApplicationCache = (): void => {
  try {
    if ('applicationCache' in window) {
      const appCache = (window as any).applicationCache;
      if (appCache) {
        appCache.update();
        if (appCache.status === appCache.UPDATEREADY || appCache.status === appCache.IDLE) {
          appCache.swapCache();
        }
      }
    }
  } catch (error) {
    console.error('Error clearing Application Cache:', error);
  }
};

/**
 * Clear Redux Persist storage explicitly
 */
export const clearReduxPersistStorage = (): void => {
  try {
    // Redux Persist stores data with key "persist:root" (or custom key from persistConfig)
    // Clear all persist keys to be thorough
    const persistKeys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('persist:')) {
        persistKeys.push(key);
      }
    }
    
    // Remove all persist keys
    persistKeys.forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (error) {
        console.warn(`⚠️ Failed to remove Redux Persist key ${key}:`, error);
      }
    });
    
    if (persistKeys.length > 0) {
    }
  } catch (error) {
    console.error('Error clearing Redux Persist storage:', error);
  }
};

/**
 * Clear all browser storage types
 * This is the main function to call during logout
 */
export const clearAllStorage = async (): Promise<void> => {
  
  try {
    // Reset search analytics sessionId (1h TTL) before wiping storage
    resetSearchAnalyticsSession();

    // Clear Redux Persist storage first (before clearing all localStorage)
    clearReduxPersistStorage();
    
    // Clear localStorage (this will also clear any remaining persist data)
    try {
      localStorage.clear();
    } catch (error) {
      console.error('Error clearing localStorage:', error);
    }
    
    // Clear sessionStorage
    try {
      sessionStorage.clear();
    } catch (error) {
      console.error('Error clearing sessionStorage:', error);
    }
    
    // Clear all cookies
    clearAllCookies();
    
    // Clear Cache API (runs in parallel with other async operations)
    const cachePromise = clearAllCaches();
    
    // Clear Service Worker cache
    const swPromise = clearServiceWorkerCache();
    
    // Clear IndexedDB
    const idbPromise = clearAllIndexedDB();
    
    // Wait for all async operations
    await Promise.all([cachePromise, swPromise, idbPromise]);
    
    // Clear Application Cache (synchronous)
    clearApplicationCache();
  } catch (error) {
    console.error('Error during comprehensive storage clear:', error);
    // Don't throw - we want to continue with logout even if some storage clearing fails
  }
};

